// src/i18n/useTranslationStatus.ts
//
// Realtime hook for a language's translation generation status.
// Subscribes to translations/{language_code} via Firestore's
// onSnapshot, keeps the in-memory dictionary in sync as new
// strings land, and exposes ready/generating/progress for the
// standby screen.
//
// Usage:
//
//     const { ready, generating, total, completed } =
//       useTranslationStatus(language);
//     if (!ready) return <TranslationStandbyScreen
//                            progress={completed} total={total} />;
//
// The hook also issues the initial trigger (via loadTranslations)
// so callers don't need to coordinate that separately. Calling it
// for English short-circuits — no Firestore listener, no work.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { installFromSnapshot, loadTranslations } from './loadTranslations';
import { getDictionary } from './useT';
import { LanguageEntry } from './languages';

export interface TranslationStatus {
  /** True when the dictionary is fully loaded and not generating.
   *  English is always ready. */
  ready: boolean;
  /** True while a Cloud Function generation job is running. The
   *  partial dictionary IS already installed and growing — it's
   *  fine to show the app, but the standby screen exists for the
   *  cleaner UX. */
  generating: boolean;
  /** Total number of strings in the source dictionary. 0 for
   *  English (or before the first doc-snap arrives). */
  total: number;
  /** Strings translated so far. Equals `total` once ready. */
  completed: number;
  /** Set if anything went wrong with the trigger or listener. The
   *  gate falls back to English and dismisses on error so the
   *  user isn't blocked. */
  error: string | null;
}

const READY_FOR_ENGLISH: TranslationStatus = {
  ready: true,
  generating: false,
  total: 0,
  completed: 0,
  error: null,
};

const INITIAL_NON_ENGLISH: TranslationStatus = {
  ready: false,
  generating: true,
  total: 0,
  completed: 0,
  error: null,
};

/**
 * Module-level "ready" latch. Once a non-English language reaches
 * a ready state in this session, we remember it and short-circuit
 * the gate immediately on subsequent navigations — no LanguageGate
 * flicker on route changes, no remount of the entire children tree.
 *
 * This was the root cause of the "app keeps refreshing on first
 * load or nav" symptom: every effect re-run reset the status to
 * INITIAL_NON_ENGLISH (ready:false), the gate flipped to render
 * the standby screen for a tick before the next snapshot landed,
 * and React unmounted+remounted the whole app subtree under it.
 *
 * Stored as a Set of language codes so a future language switch
 * (different code) still flows through the standby screen the
 * first time. Cleared on hard reload (which is what setLanguage
 * triggers anyway).
 */
const readyLanguages = new Set<string>();

export function useTranslationStatus(
  language: LanguageEntry | null
): TranslationStatus {
  // Compute the initial status synchronously so the very first
  // render doesn't flash "not ready" — three sources, in order:
  //   1. English / null → ready (no listener at all).
  //   2. Module ready-latch already says this language is ready
  //      (i.e. user is navigating around an already-loaded app)
  //      → ready, skip the listener-warmup hop.
  //   3. Dictionary already has entries from a previous mount
  //      (e.g. effect re-run in StrictMode) → ready as well.
  //   4. Otherwise INITIAL_NON_ENGLISH and let the listener fill
  //      in real progress.
  const [status, setStatus] = useState<TranslationStatus>(() => {
    if (!language || language.code === 'en') return READY_FOR_ENGLISH;
    if (readyLanguages.has(language.code)) return READY_FOR_ENGLISH;
    const dict = getDictionary();
    const entryCount = Object.keys(dict.entries || {}).length;
    if (entryCount > 0) {
      return {
        ready: true,
        generating: false,
        total: entryCount,
        completed: entryCount,
        error: null,
      };
    }
    return INITIAL_NON_ENGLISH;
  });

  useEffect(() => {
    const code = language?.code ?? null;

    // English / null short-circuit — no listener, no trigger.
    if (!language || code === 'en') {
      setStatus(READY_FOR_ENGLISH);
      return;
    }

    // Already-ready short-circuit. Avoid resetting status to
    // INITIAL_NON_ENGLISH because that flickers the gate on every
    // re-mount and causes the app subtree to unmount/remount.
    if (readyLanguages.has(code)) {
      setStatus((prev) =>
        prev.ready
          ? prev
          : {
              ready: true,
              generating: false,
              total: prev.total,
              completed: prev.completed,
              error: null,
            }
      );
      // Still subscribe — we want incremental updates on future
      // generation refreshes — but we DON'T flip ready off.
    } else {
      // First time we see this language; seed the standby UI.
      setStatus(INITIAL_NON_ENGLISH);
    }

    // Kick off the initial load + trigger.
    let cancelled = false;
    loadTranslations(language)
      .then((res) => {
        if (cancelled) return;
        const newReady = res.ready || readyLanguages.has(code);
        if (res.ready) readyLanguages.add(code);
        setStatus({
          ready: newReady,
          generating: res.generating,
          total: res.total,
          completed: res.completed,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('useTranslationStatus: loadTranslations failed:', err);
        // Don't strand the user — degrade to ready (English
        // fallback at runtime).
        setStatus({
          ready: true,
          generating: false,
          total: 0,
          completed: 0,
          error: (err as Error).message ?? 'load failed',
        });
      });

    // Subscribe to the doc for live progress updates.
    const ref = doc(db, 'translations', language.code);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          // Doc not created yet — keep current status; the
          // generation trigger will create it shortly.
          return;
        }
        const data = snap.data() as any;
        // Re-install on every snap so newly-translated strings
        // become visible without waiting for completion.
        installFromSnapshot(data);
        const total = data.total ?? 0;
        const completed = data.completed ?? 0;
        const generating = !!data.generating;
        // Sticky ready: once we've ever been ready in this
        // session for this language, never flip back to false.
        // Translation progress is monotonic; a transient snapshot
        // where total temporarily exceeds completed (e.g. the
        // function bumped total before completed in two writes)
        // shouldn't unmount the children.
        const isReadyNow = completed >= total && total > 0;
        if (isReadyNow) readyLanguages.add(code);
        const ready = isReadyNow || readyLanguages.has(code);
        setStatus({
          ready,
          generating,
          total,
          completed,
          error: null,
        });
      },
      (err) => {
        if (cancelled) return;
        console.warn('useTranslationStatus: snapshot listener error:', err);
        setStatus((prev) => ({ ...prev, error: err.message }));
      }
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [language?.code]);

  return status;
}
